import { NavLink, useNavigate } from "react-router-dom";
import { 
  LayoutDashboard, Users, MapPin, UserPlus, FileText, LogOut, 
  ChevronLeft, ChevronRight, X, Menu, BookOpen 
} from "lucide-react";
import { getCurrentUser, logoutService } from '../services/authService';

export default function Sidebar({ isOpen, setIsOpen, logo }) {
  const navigate = useNavigate();
  const user = getCurrentUser();

  // Get user initials for avatar
  const getUserInitials = (name) => {
    if (!name) return 'U';
    const names = name.split(' ');
    if (names.length >= 2) {
      return names[0][0].toUpperCase() + names[1][0].toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const handleLogout = () => {
    logoutService();
    navigate("/login");
  }; 

  const navItems = [
    { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
    { to: "/regions", icon: MapPin, label: "Regions" },
    { to: "/enumerators", icon: UserPlus, label: "Enumerators" },
    { to: "/assign-users", icon: Users, label: "Assign Users" },
    { to: "/surveys", icon: FileText, label: "Surveys" },
    { to: "/knowledge", icon: BookOpen, label: "Knowledge" },
  ];

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div 
          className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}
      
      {/* Mobile Menu Toggle */}
      <button
        className="md:hidden fixed top-4 left-4 z-50 p-2 bg-white rounded-lg shadow-lg border border-gray-200"
        onClick={() => setIsOpen(true)}
      >
        <Menu size={24} />
      </button>

      <aside
        className={`
          fixed md:relative
          ${isOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"} 
          ${isOpen ? "w-64" : "w-20"} 
          bg-white border-r border-gray-200 
          transition-all duration-300 ease-in-out
          flex flex-col h-full
          z-50 md:z-auto
          h-screen md:h-auto
        `}
      >
        <div className="p-4 flex items-center justify-between border-b">
          {isOpen ? (
            <img src={logo} alt="Indomie" className="h-10 object-contain" />
          ) : (
            <div className="text-xl font-bold text-orange-500">IM</div>
          )}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="p-2 rounded-lg hover:bg-gray-100 flex-shrink-0"
          >
            {isOpen ? (
              <>
                <ChevronLeft size={20} className="hidden md:block" />
                <X size={20} className="md:hidden" />
              </>
            ) : (
              <ChevronRight size={20} />
            )}
          </button>
        </div>
        <nav className="flex-1 px-3 py-6 space-y-1 overflow-y-auto">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={() => setIsOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-3 rounded-lg transition-colors ${
                  isActive
                    ? "bg-orange-50 text-orange-600"
                    : "text-gray-700 hover:bg-gray-100"
                } ${!isOpen && "justify-center"}`
              }
            >
              <item.icon size={isOpen ? 22 : 24} />
              {isOpen && <span className="font-medium">{item.label}</span>}
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t mt-auto">
          {/* User Details */}
          <div className={`mb-3 ${!isOpen && "text-center"}`}>
            <div className={`flex items-center gap-3 ${!isOpen && "justify-center"}`}>
              <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center text-orange-600 font-bold flex-shrink-0">
                {getUserInitials(user?.name)}
              </div>
              {isOpen && (
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{user?.name || 'Admin User'}</p>
                  <p className="text-xs text-gray-500 truncate">{user?.email || 'admin@example.com'}</p>
                </div>
              )}
            </div>
          </div>
          
          {/* Logout Button */}
          <button
            onClick={handleLogout}
            className={`flex items-center gap-3 px-3 py-3 rounded-lg text-red-600 hover:bg-red-50 w-full transition-colors ${
              !isOpen && "justify-center"
            }`}
          >
            <LogOut size={22} />
            {isOpen && <span className="font-medium">Logout</span>}
          </button>
        </div>
      </aside>
    </>
  );
}